import { Scene, Mesh, PlaneGeometry } from 'three';
import BackgroundPostProcess from './BackgroundPostProcess.js';
import Camera from './Camera.js';
import Sizes from '../utils/Sizes.js';
import Phone from './Phone.js';

export default class ForegroundScene {
    constructor() {
        // Singleton pattern
        if (ForegroundScene.instance) {
            return ForegroundScene.instance;
        }
        ForegroundScene.instance = this;

        // Create scene
        this.scene = new Scene();

        this.camera = Camera.getInstance().cameraForeground;
        this.sizes = Sizes.getInstance();

        // Initialize components
        this.initBackgroundPlane();
        this.initPhone();

        // Handle resize events
        this.sizes.on('resize', () => this.resize());
    }

    // Static method to get the instance
    static getInstance() {
        if (!ForegroundScene.instance) {
            ForegroundScene.instance = new ForegroundScene();
        }
        return ForegroundScene.instance;
    }

    calculateVisiblePlaneSize(distance) {
        // Calculate visible plane size at given distance from camera
        // Distance is measured from the camera position
        const fov = this.camera.fov * (Math.PI / 180); // Convert to radians

        // Get the height of the visible area at the specified distance
        const height = 2 * Math.tan(fov / 2) * Math.abs(distance);
        const width = height * this.sizes.aspectRatio;

        return {
            width: width,
            height: height
        };
    }

    calculatePlaneDistance() {
        // Calculate the distance from camera to background plane
        // Get camera position z coordinate
        const cameraZ = this.camera.position.z;

        // Position the plane behind the camera's target point
        // The camera is looking at z=0, so we want the plane at a negative z value
        const planeZ = -cameraZ; // Mirror position to place it behind the target

        // The actual distance is from camera position to plane position
        return Math.abs(cameraZ - planeZ);
    }

    initBackgroundPlane() {
        // Get camera position and calculate the optimal distance
        const planeZ = -this.camera.position.z; // Position the plane behind the camera target point

        // Calculate the total distance from camera to plane
        const planeDistance = this.calculatePlaneDistance();

        // Calculate required plane size based on this distance
        const { width, height } = this.calculateVisiblePlaneSize(planeDistance);

        // Create a plane to display the processed background
        const geometry = new PlaneGeometry(width, height);

        this.postProcess = new BackgroundPostProcess();
        this.material = this.postProcess.getMaterial();

        this.backgroundPlane = new Mesh(geometry, this.material);
        this.backgroundPlane.position.z = planeZ; // Position it behind the camera target
        this.scene.add(this.backgroundPlane);
    }

    initPhone() {
        // Create a phone object for the foreground
        this.phone = new Phone();
        this.phone.position.z = 0.5;
        this.scene.add(this.phone);
    }

    resize() {
        // Update background plane size on resize
        if (this.backgroundPlane) {
            const planeDistance = this.calculatePlaneDistance();
            const { width, height } = this.calculateVisiblePlaneSize(planeDistance);
            this.backgroundPlane.geometry.dispose();
            this.backgroundPlane.geometry = new PlaneGeometry(width, height);
            if (this.backgroundPlane.material.uniforms.uResolution) {
                this.backgroundPlane.material.uniforms.uResolution.value.set(this.sizes.width, this.sizes.height);
            }
        }
    }

    update(delta) {
        // Update the phone
        if (this.phone) {
            this.phone.update(delta);
        }

        // Update postProcessing
        if (this.postProcess) {
            this.postProcess.update(delta);
        }
    }

    getScene() {
        return this.scene;
    }

    updateBackgroundTexture(texture) {
        if (this.backgroundPlane && this.backgroundPlane.material.uniforms.tDiffuse) {
            this.backgroundPlane.material.uniforms.tDiffuse.value = texture;
            this.backgroundPlane.material.needsUpdate = true;
        }
    }
}
